import { defineStore } from "pinia";
import joi from "joi";
import {
  fromUnixToAPIDate,
  getEndDate,
  getStartDate,
  toUnix,
} from "@/stories/utils/dates";
import { useAuthStore } from "@/stores/auth.module";
import md5 from "md5";
import type { IDefaultStoreActions, IVideoState } from "@/interfaces";
import type {
  StoreName,
  TabErrorSchemaTypes,
  EditorModel,
  IdType,
  RRuleSupportedEntityType,
  DefaultFilterType,
  ParticipantData,
  DefaultState,
  PreviewBasedState,
} from "@/types";
import helpers from "@/helpers";
import {
  type NotificationSettingsInput,
  type ActivityInput,
  type SummarizedActivitiesFragment,
  Group_Connection,
  Module_Type,
  type ActivityFragment,
  type CompactInstitutionFragment,
} from "@/graphql/generated-types";
import { fixValidationFields } from "@/helpers/forms/validate";
import { globali18n } from "@/i18n";
import { DEFAULT_NOTIFICATION_SETTINGS } from "@/consts";
import PagerManager from "@/helpers/manager/PagerManager";
import api from "@/api";
import getMaxDescriptionLength from "@/helpers/environment/getMaxDescriptionLength";
import { useDefaults } from "@/composables/useDefaults";
import toVideoList from "@/helpers/files/toVideoList";
import getNotificationSettings from "@/services/notications/getNotificationSettings";
import getVisibilityOptions from "@/helpers/visibility/getVisibilityOptions";
import {
  PDF_FEATURE,
  type PDFFeatureState,
} from "@/helpers/features/PDFFeature";
import {
  PICTURES_FEATURE,
  type PicturesFeatureState,
} from "@/helpers/features/PicturesFeature";
import {
  SIGNUP_FEATURE,
  type SignupFeatureState,
} from "@/helpers/features/SignupFeature";
import {
  SHARING_FEATURE,
  type SharingFeatureState,
} from "@/helpers/features/SharingFeature";
import {
  RRULE_FEATURE,
  type RRuleFeatureState,
} from "@/helpers/features/RRuleFeature";
import ParticipantFeature, {
  type ParticipantFeatureState,
} from "@/helpers/features/ParticipantFeature";
import {
  EXTERNAL_EVENT_FEATURE,
  type ExternalEventFeatureState,
} from "@/helpers/features/ExternalEventFeature";
import getTabCounts from "@/helpers/shared/getTabCounts";
import toPreviewBasedState from "@/helpers/shared/toPreviewBasedState";

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
  description: joi.string().max(getMaxDescriptionLength()).required(),
  startDate: joi.number().required(),
  endDate: joi.number().required().greater(joi.ref("startDate")),
  imageList: joi.array().min(1).max(30),
  documentToUpload: joi
    .array()
    .optional()
    .items(
      joi.object({
        name: joi.string().required().max(50).min(1),
      }),
    ),
  price: joi.number().max(9999999),
  ...SIGNUP_FEATURE.getValidationRules(),
});

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title", "description", "endDate", "maxSignups", "price"],
  pictures: ["imageList"],
  pdf: ["documentToUpload.*.name"],
};

const participantFeature = new ParticipantFeature<ParticipantData>([
  "employeesResponsible",
  "employeesSignedUp",
  "residentsResponsible",
  "residentsSignedUp",
]);

type VisibilityTypes = "now" | "beforeXMinutes" | "onStartTime";

export type ActivitiesItem = {
  id?: number;
  title: string;
  description: EditorModel;
  location: string;
  startDate: number;
  endDate: number;
  pictograms: string[];
  pdf?: string | null | undefined;
  showFrom: null | number | 0; // null = visible from now | number = minutes before start date | 0 = from start time
  visibilityType: VisibilityTypes;
  visibleFromDays: number;
  visibleFromHours: number;
  visibleFromMinutes: number;
  institution?: CompactInstitutionFragment;
  videos: IVideoState[];
  price?: string;
  vacation?: boolean;
  category?: string;
  isCancelled: boolean;
  __typename: RRuleSupportedEntityType;
  isVisible: boolean;
  original: Partial<ActivitiesItem>;
} & PDFFeatureState &
  PicturesFeatureState &
  SignupFeatureState &
  SharingFeatureState &
  Omit<RRuleFeatureState, "original"> &
  ParticipantFeatureState<ParticipantData>;

export type ActivityListFilters = {
  categories: PreviewBasedState<string[]>;
  departments: PreviewBasedState<string[]>;
  groups: PreviewBasedState<string[]>;
};

export type ActivitiesState = {
  listFilters: ActivityListFilters;
  item: ActivitiesItem;
  notification: NotificationSettingsInput;
} & DefaultState &
  ExternalEventFeatureState<SummarizedActivitiesFragment>;

export interface IActivitiesActions extends IDefaultStoreActions {
  setItemVisibility(visibility: VisibilityTypes): void;
}

const getDefaultListFilters = (): ActivityListFilters => {
  return {
    categories: toPreviewBasedState([]),
    departments: toPreviewBasedState([]),
    groups: toPreviewBasedState([]),
  };
};

const getDefaultState = (): ActivitiesState => {
  const item: ActivitiesItem = {
    title: "",
    description: helpers.quill.getEmptyEditorModel(),
    location: "",
    pdf: undefined,
    visibilityType: "now",
    visibleFromDays: 0,
    visibleFromHours: 0,
    visibleFromMinutes: 0,
    showFrom: null,
    videos: [],
    pictograms: [],
    category: undefined,
    isCancelled: false,
    isVisible: false,
    original: {
      startDate: getStartDate(),
      endDate: getEndDate(),
      isCancelled: false,
    },
    ...PDF_FEATURE.getDefault(),
    ...PICTURES_FEATURE.getDefault(),
    ...SIGNUP_FEATURE.getDefault(),
    ...SHARING_FEATURE.getDefault(),
    ...RRULE_FEATURE.getDefault("SingleActivity"),
    ...participantFeature.getDefault(),
  };

  return {
    listFilters: getDefaultListFilters(),
    tabErrors: {},
    originalHash: null,
    validation: null,
    pager: new PagerManager<SummarizedActivitiesFragment>(),
    item,
    notification: {
      ...DEFAULT_NOTIFICATION_SETTINGS,
    },
    listResourceType: "internal",
  };
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): ActivitiesState => getDefaultState(),
    getters: {
      hasCancellationStatusChanged(state) {
        return state.item.original.isCancelled !== state.item.isCancelled;
      },
      isRescheduled(state) {
        return (
          state.item.original.startDate !== state.item.startDate ||
          state.item.original.endDate !== state.item.endDate
        );
      },
      willBeCancelled(state) {
        return (
          state.item.original.isCancelled !== state.item.isCancelled &&
          state.item.isCancelled === true
        );
      },
      willBeReactivated(state) {
        return (
          state.item.original.isCancelled !== state.item.isCancelled &&
          state.item.isCancelled === false
        );
      },
      responsibleCount(state) {
        return [
          ...state.item.participant.data.employeesResponsible,
          ...state.item.participant.data.residentsResponsible,
        ].length;
      },
      participantCount(state) {
        return [
          ...state.item.participant.data.employeesSignedUp,
          ...state.item.participant.data.residentsSignedUp,
        ].length;
      },
      tabCounts: (state) => getTabCounts(state),
    },
    actions: {
      hasUnsavedChanges() {
        return !!(
          this.item &&
          this.originalHash &&
          this.originalHash !== this.currentHash()
        );
      },
      currentHash(): string {
        const data = {
          ...this.getItem(),
          notification: this.notification,
          videos: this.item.videos,
          ...PDF_FEATURE.hash(this.item),
          ...PICTURES_FEATURE.hash(this.item),
          ...SHARING_FEATURE.hash(this.item),
          ...RRULE_FEATURE.hash(this.item as RRuleFeatureState),
        };
        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        this.originalHash = this.currentHash();
      },
      async setItem(id: IdType) {
        // No need to refetch
        if (id && this.item.id?.toString() === id.toString()) {
          return;
        }

        let data: ActivityFragment | undefined;

        if (this.listResourceType === "internal") {
          data = await api.activities.getActivityDetail(id);
        } else {
          data = await api.activities.getExternalActivityDetail(id);
        }

        if (!data) {
          return;
        }

        this.item = {
          ...getDefaultState().item,
          id: parseInt(data.id),
          title: data.title,
          price: data?.price?.[0]?.price
            ? (data.price[0].price / 100).toString()
            : undefined,
          location: data.location || "",
          description: helpers.quill.htmlToEditorModel(data.description || ""),
          startDate: toUnix(new Date(data.startDate)),
          endDate: toUnix(new Date(data.endDate)),
          videos: toVideoList(data),
          isCancelled: data.cancelled,
          pictograms: (data.pictograms || [])?.map((item) => item.id),
          __typename: data.__typename as RRuleSupportedEntityType,
          institution: data.institution,
          externalContentVisibility: undefined,
          original: {
            startDate: toUnix(new Date(data.startDate)),
            endDate: toUnix(new Date(data.endDate)),
            isCancelled: data.cancelled,
          },
          ...PICTURES_FEATURE.getState(data),
          ...getVisibilityOptions({
            showFrom: data.showFrom,
            startDate: this.item.startDate,
            endDate: this.item.endDate,
          }),
          ...participantFeature.getItem(data),
        };

        this.item.category = data.category?.id || undefined;

        SHARING_FEATURE.setState({
          data,
          state: this.item,
          moduleType: Module_Type.Activity,
          groupConnection: Group_Connection.Activity,
          listResourceType: this.listResourceType,
          onCompleted: this.setOriginalHash,
        });

        RRULE_FEATURE.setState(data, this.item as RRuleFeatureState);

        PDF_FEATURE.setState(data, this.item);

        // Signup options
        SIGNUP_FEATURE.setState(data, this.item);

        const { getActivityNotificationSettings } = useDefaults();
        this.notification = getNotificationSettings(
          data,
          this.item,
          getActivityNotificationSettings,
        );

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): ActivityInput {
        const authStore = useAuthStore();
        const item = { ...this.item };

        const id = item?.id?.toString();

        if (item.visibilityType === "now") {
          item.showFrom = null;
        } else if (item.visibilityType === "onStartTime") {
          item.showFrom = 0;
        } else {
          const daysInMinutes = item.visibleFromDays
            ? item.visibleFromDays * 1440
            : 0;
          const hoursInMinutes = item.visibleFromHours
            ? item.visibleFromHours * 60
            : 0;
          const minutes = item.visibleFromMinutes ? item.visibleFromMinutes : 0;

          item.showFrom = daysInMinutes + hoursInMinutes + minutes;
        }

        const input: ActivityInput = {
          id,
          institution:
            item.institution?.id?.toString() || authStore.institutionId,
          title: item.title,
          status: item.isCancelled ? 2 : 1,
          pictograms: item.pictograms,
          pdf: item.pdf,
          startDate: fromUnixToAPIDate(item.startDate),
          endDate: fromUnixToAPIDate(item.endDate),
          showFrom: item.showFrom,
          location: item.location,
          // We have to send NULL to make the field empty. Undefined doesn't work
          category: item.category || null,
          description: helpers.quill.editorModelToHtml(item.description),
          ...(item.price && { price: parseFloat(item.price) }),
          image: undefined,
          ...SIGNUP_FEATURE.getItem(this.item),
          ...SHARING_FEATURE.getItem(this.item),
          ...RRULE_FEATURE.getItem(this.item as RRuleFeatureState),
        };

        // We should only save those values if they're changed
        participantFeature.append(input, this.item);

        return input;
      },
      async hasStaleData() {
        return await participantFeature.hasStaleData(this.item, () =>
          api.activities.getActivityDetail(this.item.id),
        );
      },
      setItemVisibility(visibility: VisibilityTypes) {
        this.item.visibilityType = visibility;
      },
      async isNotValid() {
        this.validation = helpers.forms.validate(VALIDATION_SCHEMA, {
          ...this.item,
          description: helpers.quill.editorModelToText(this.item.description),
          ...PDF_FEATURE.toValidation(this.item),
          ...PICTURES_FEATURE.toValidation(this.item),
        });

        // We can't use usei18n() in stores.
        // https://stackoverflow.com/a/71449708
        const { t } = globali18n;

        // We should fix the validation labels
        fixValidationFields(this.validation, {
          startDate: t("global.start"),
        });

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
      async fetchItems(filter: DefaultFilterType) {
        await EXTERNAL_EVENT_FEATURE.fetchItems(
          this,
          filter,
          api.activities.getActivities,
          api.activities.getExternalActivities,
        );
      },
    },
  });
};

// Activities View store
export const useActivitiesStore = createUseStore("ACTIVITIES");

// We use the same store structure for the create modal.
export const useAddActivitiesStore = createUseStore("ADD_ACTIVITIES");

export type ActivitiesStore = ReturnType<typeof useActivitiesStore>;
